import type { SpreadsheetTarget } from "@/types/evidences";
import type { BusinessReportOutcomeEvidenceResult } from "@/modules/snapshots/businessReports.types";
import type { CitationInfo } from "@/modules/chat/chat-ui.types";

const SPREADSHEET_CONTENT_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
  "application/vnd.ms-excel.template.macroenabled.12",
  "application/vnd.oasis.opendocument.spreadsheet-flat-xml",
  "application/vnd.oasis.opendocument.spreadsheet-template",
  "text/spreadsheet",
  "application/vnd.uof.spreadsheet",
  "application/x-pocket-excel",
]);

const SPREADSHEET_EXTENSIONS = [
  ".xlsx",
  ".xls",
  ".xlsm",
  ".xlsb",
  ".xlt",
  ".xltm",
  ".xltx",
  ".xlw",
  ".ods",
  ".fods",
  ".ots",
  ".slk",
  ".uos",
  ".pxl",
];

interface SourceDocumentInput {
  contentType?: string | null;
  documentName?: string | null;
}

export const getInitialSourceFseId = (
  outcomeFseId: string | null | undefined,
  evidences: ReadonlyArray<{ fseId: string }>,
): string | null => outcomeFseId ?? evidences[0]?.fseId ?? null;

export const isSpreadsheetSource = ({ contentType, documentName }: SourceDocumentInput): boolean => {
  if (contentType) {
    return SPREADSHEET_CONTENT_TYPES.has(contentType.toLowerCase());
  }

  const lowerName = documentName?.toLowerCase() ?? "";
  return SPREADSHEET_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

export const toSpreadsheetTarget = (
  target: BusinessReportOutcomeEvidenceResult["spreadsheet_target"],
): SpreadsheetTarget | undefined => {
  if (!target) return undefined;

  return {
    sheetName: target.sheet_name,
    cellRef: target.cell_ref,
    sourceValue: target.source_value,
    matchKind: target.match_kind,
  };
};

export const isEvidenceForPrompt = (evidencePromptId: string | null | undefined, promptId: string | null): boolean =>
  !promptId || evidencePromptId === promptId;

export const getActiveSourceCitation = (
  currentCitation: CitationInfo | null,
  fsEntryId: string | null | undefined,
): CitationInfo | null => {
  if (!currentCitation || !fsEntryId || currentCitation.fseId !== fsEntryId) {
    return null;
  }

  return currentCitation;
};
